import { PromptModel } from "./prompt-model";
import {
  AssemblyReferenceType,
  AssemblyReferenceCondition,
  AssemblyReferenceConditionWrite,
} from "./assembly-condition";
import {
  AssemblyReferenceTemplateColumn,
  AssemblyReferenceTemplateColumnWrite,
  RichTextTemplate,
} from "./assembly-template";

/** How a "List As" reference renders the list of answers across documents. */
export enum EMultiDocumentListFormat {
  BulletedList = "bulletList",
  NumberedList = "numberedList",
  Table = "table",
  Paragraph = "paragraph",
}

/** What a single-answer reference inserts. */
export enum ESingleDocumentReferenceFormat {
  Document = "documentName",
  Quote = "quote",
  Answer = "answer",
}

/** How a single-answer reference renders a list-of-values answer. */
export enum ESingleDocumentListFormat {
  BulletedList = "bullet",
  NumberedList = "numbered",
  Text = "text",
}

/** A prompt an assembly reference draws on. */
export interface AssemblyReferencePrompt {
  objectIdentifier: string;
  label?: string;
  promptDataType?: import("./prompt-model").PromptDataType;
}

/**
 * An assembly reference as returned by the API. Normalised across the
 * Playbook Marketplace and Projects APIs. A reference draws on one-or-many
 * prompts (`prompts`); `promptObjectIdentifier`/`promptLabel` are convenience
 * views of the first prompt for single-prompt references and display.
 */
export interface AssemblyReferenceModel {
  objectIdentifier: string;
  stableObjectIdentifier: string;
  /** All prompts the reference draws on (one for non-table references). */
  prompts: AssemblyReferencePrompt[];
  /** First prompt's identifier — convenience for single-prompt references. */
  promptObjectIdentifier: string;
  promptLabel?: string;
  /**
   * Group the reference's prompt belongs to. Flow responses expose this so the
   * editor can load the group's prompts in edit mode (there is no group picker
   * then) and resolve each condition's target prompt.
   */
  groupObjectIdentifier?: string;
  /** Flow only. */
  assemblyReferenceType?: AssemblyReferenceType;
  multiDocumentListFormat?: EMultiDocumentListFormat;
  singleDocumentReferenceFormat?: ESingleDocumentReferenceFormat;
  singleDocumentAnswerListFormat?: ESingleDocumentListFormat;
  /** "List As" template (rich-text JSON). */
  template?: RichTextTemplate | null;
  /** Per-column templates when multiDocumentListFormat === Table. */
  templateColumns: AssemblyReferenceTemplateColumn[];
  conditions: AssemblyReferenceCondition[];
  /** Server-expanded prompt (Playbook responses only). */
  filter?: PromptModel;
}

/**
 * The payload the UI assembles to create or update an assembly reference.
 * The API client maps `promptObjectIdentifier` and the source-specific
 * fields to the correct request shape for each backend.
 */
export interface AssemblyReferenceWriteModel {
  /**
   * The prompt(s) this reference draws on. Exactly one for a single-document or
   * non-table list reference; the prompts the columns mention for a table.
   */
  promptObjectIdentifiers: string[];
  /** Flow only: flow-wide vs per-document. */
  assemblyReferenceType?: AssemblyReferenceType;
  multiDocumentListFormat?: EMultiDocumentListFormat;
  singleDocumentReferenceFormat?: ESingleDocumentReferenceFormat;
  singleDocumentAnswerListFormat?: ESingleDocumentListFormat;
  /** "List As" template (rich-text JSON). */
  template?: RichTextTemplate | null;
  /** Per-column templates for Table list format. Sent inline on create. */
  templateColumns?: AssemblyReferenceTemplateColumnWrite[];
  /** Conditions gating this reference. Sent inline on create. */
  conditions?: AssemblyReferenceConditionWrite[];
}
